import { SurveyUserRoles } from '@constants/user';
import { ColumnDef, Table } from '@g17eco/molecules/table';
import { getDefaultScope, getScopeModules } from '@utils/survey-scope';
import { SurveyDelegationProps } from '../types';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { useState } from 'react';
import { DeleteModal } from '@g17eco/molecules/confirm-modal';
import { SURVEY } from '@constants/terminology';
import { useRemoveSurveyWorkgroupMutation } from '@api/workgroups';
import { generateErrorToast, generateToast } from '@g17eco/molecules/toasts';
import { DelegateWorkgroupModal } from './DelegateWorkgroupModal';
import { SurveyWorkgroup } from '@g17eco/types/workgroup';
import { DataScopeAccess } from '@g17eco/types/surveyDataScope';
import { WorkgroupIcon } from '../../../../../../molecules/workgroup/WorkgroupIcon';

const getQuestionCount = (
  { permission, utrvIds: workgroupUtrvIds }: SurveyWorkgroup,
  role: SurveyUserRoles.Stakeholder | SurveyUserRoles.Verifier,
  utrvIds: string[],
) => {
  if (!permission.roles.includes(role)) {
    return '-';
  }

  return utrvIds.length === workgroupUtrvIds.length ? 'All' : workgroupUtrvIds.length;
};

interface Props extends Pick<SurveyDelegationProps, 'survey' | 'metricGroups' | 'utrvIds'> {
  workgroups: SurveyWorkgroup[];
}

export const WorkgroupsTable = ({ workgroups, survey, metricGroups, utrvIds }: Props) => {
  const [deleteWorkgroup] = useRemoveSurveyWorkgroupMutation();
  const [deleting, setDeleting] = useState<SurveyWorkgroup | undefined>();
  const [editing, setEditing] = useState<SurveyWorkgroup | undefined>();

  const handleDelete = () => {
    if (!deleting) {
      return;
    }

    deleteWorkgroup({ surveyId: survey._id, workgroupId: deleting._id })
      .unwrap()
      .then(() => {
        generateToast({
          title: 'Workgroup removed',
          color: 'success',
          message: 'Workgroup has been removed from the survey',
        });
      })
      .catch((e) => {
        generateErrorToast(e);
      })
      .finally(() => {
        setDeleting(undefined);
      });
  };

  const columns: ColumnDef<SurveyWorkgroup>[] = [
    {
      header: 'Workgroup',
      meta: {
        cellProps: {
          className: 'dont_translate',
        },
      },
      accessorKey: 'name',
      cell: ({ row }) => {
        const workgroup = row.original;
        return (
          <div>
            <WorkgroupIcon workgroup={workgroup} />
            {workgroup.name}
          </div>
        );
      },
    },
    {
      id: 'isAdmin',
      header: () => <i className='fal fa-user-gear text-ThemeIconSecondary' />,
      cell: ({ row }) => {
        const workgroup = row.original;
        return workgroup.permission.roles.includes(SurveyUserRoles.Admin) ? (
          <i className='fal fa-check text-ThemeSuccessMedium' />
        ) : (
          <i className='fal fa-xmark text-ThemeIconSecondary' />
        );
      },
    },
    {
      id: 'contributingMetricsCount',
      header: () => {
        return <i className='fa fa-user-edit text-primary' />;
      },
      accessorFn: (workgroup) => {
        return getQuestionCount(workgroup, SurveyUserRoles.Stakeholder, utrvIds);
      },
    },
    {
      id: 'verifyingMetricsCount',
      header: () => {
        return <i className='fa fa-user-check text-success' />;
      },
      accessorFn: (workgroup) => {
        return getQuestionCount(workgroup, SurveyUserRoles.Verifier, utrvIds);
      },
    },
    {
      id: 'modules',
      header: 'Modules',
      cell: ({ row }) => {
        const workgroup = row.original;
        const access = workgroup.permission.access;
        const scope =
          (access === DataScopeAccess.Full ? survey.scope : workgroup.permission.scope) ?? getDefaultScope();
        const modules = getScopeModules({ scope, metricGroups });

        switch (modules.length) {
          case 0:
            return '-';
          case 1:
            return modules[0].name;
          default:
            return (
              <SimpleTooltip text={modules.map((module) => module.name).join(', ')}>
                <i className='fal fa-info-circle text-ThemeIconSecondary' />
              </SimpleTooltip>
            );
        }
      },
    },
    {
      id: 'buttons',
      header: '',
      cell: (c) => (
        <UncontrolledDropdown>
          <DropdownToggle outline color='transparent' className='border-ThemeNeutralsLight px-2'>
            <i className={'fal fa-bars'}></i>
          </DropdownToggle>
          <DropdownMenu className='border-ThemeBorderDefault'>
            <DropdownItem onClick={() => setEditing(c.row.original)}>
              <i className='fal fa-pencil mr-2' />
              Edit
            </DropdownItem>
            <DropdownItem onClick={() => setDeleting(c.row.original)}>
              <i className='fal fa-trash mr-2 text-ThemeDangerMedium' />
              Delete
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  return (
    <>
      <div className='mt-3'>
        <Table
          columns={columns}
          data={workgroups}
          responsive
          noData={`No workgroups have been delegated to this ${SURVEY.SINGULAR}`}
        />
      </div>
      <DeleteModal
        title={`workgroup from ${SURVEY.SINGULAR}`}
        content={
          <>
            <p>
              You are about to remove the workgroup <b>{deleting?.name}</b> from this {SURVEY.SINGULAR}. This will
              remove {SURVEY.SINGULAR} access to all users who have been delegated via this workgroup.
            </p>
            <p>Are you sure you want to remove this workgroup?</p>
          </>
        }
        isOpen={!!deleting}
        handleDelete={handleDelete}
        handleCancel={() => setDeleting(undefined)}
      />
      <DelegateWorkgroupModal
        key={`delegate-workgroup-modal-${editing}`}
        isOpen={!!editing}
        toggle={() => setEditing(undefined)}
        survey={survey}
        metricGroups={metricGroups}
        delegatedWorkgroups={workgroups}
        editingWorkgroup={editing}
      />
    </>
  );
};
